import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { userRoles } from '../../../../environments/environment';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { AddCategoryComponent } from '../../../shared/components/dialog/add-category/add-category.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';

// Interface for Event Category
interface EventCategory {
  id: string;
  name: string;
  typeName: string;
  description: string;
  status: string;
  isOpen: boolean;
  isActive: boolean;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: any;
}

interface EventCategoryResponse {
  description: any;
  typeName: any;
  eventCategoryId: string;
  categoryName: string;
  id: string;
  isActive: boolean;
}

@Component({
  selector: 'app-event-categories',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonComponent],
  templateUrl: './event-category.component.html',
  styleUrls: ['./event-category.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('void', style({ height: '0', opacity: 0 })),
      state('*', style({ height: '*', opacity: 1 })),
      transition(':enter', [animate('300ms ease-out')]),
      transition(':leave', [animate('300ms ease-in')]),
    ]),
  ],
})
export class EventCategoriesComponent implements OnInit {
  search: string = '';
  eventCategories: EventCategory[] = [];
  filteredEventCategories: EventCategory[] = [];
  isLoading: boolean = false;

  userRoles = {
    addNew: false,
    listItem: {
      edit: false,
      delete: false,
    },
  };

  constructor(
    public router: Router,
    private dialog: MatDialog,
    private httpService: HttpService,
    private location: Location,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.fetchEventCategoryList().subscribe();

    const user = localStorage.getItem('userRole');
    if (user && user in userRoles) {
      this.userRoles = userRoles[user as keyof typeof userRoles]?.faqCategories;
    }
  }

  updateSearch(searchValue: string): void {
    this.search = searchValue;
    this.applyFilter();
  }

  applyFilter(): void {
    if (!this.search) {
      this.filteredEventCategories = [...this.eventCategories];
    } else {
      const searchTerm = this.search.toLowerCase();
      this.filteredEventCategories = this.eventCategories.filter((category) =>
        category.name.toLowerCase().includes(searchTerm),
      );
    }
  }

  goBack(): void {
    this.location.back();
  }

  openAddCategory(): void {
    const dialogRef = this.dialog.open(AddCategoryComponent, {
      width: '34.375rem',
      data: { initialData: undefined, EditMode: false, type: true },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.fetchEventCategoryList().subscribe();
      }
    });
  }

  toggleCategory(index: number): void {
    this.filteredEventCategories[index].isOpen =
      !this.filteredEventCategories[index].isOpen;
  }

  editCategory(category: EventCategory): void {
    const dialogRef = this.dialog.open(AddCategoryComponent, {
      width: '34.375rem',
      data: { initialData: category, EditMode: true, type: true },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.fetchEventCategoryList().subscribe();
      }
    });
  }

  statusUpdate(category: any): void {
    const currentStatus = category?.isActive;
    const newStatus = currentStatus ? 'Deactivate' : 'Activate';
    const dialogData = {
      title: 'Are you sure!',
      message: `Are you sure you want to ${newStatus} ?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '30.438rem', data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.updateStatus(category.id);
        }
      });
  }

  deleteCategory(row: any): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure want to delete?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.httpService
            .delete<ApiDataResponse>(`settings/workexperiencetype/${row}`)
            .subscribe({
              next: (response) => {
                if(response?.status){
                  this.fetchEventCategoryList().subscribe();
                  this.showSuccessToast(response?.message)
                }
              },
              error: (err) => this.showErrorToast(err?.message),
            });
        }
      });
  }

  private updateStatus(categoryId: string): void {
    this.httpService
      .put<ApiDataResponse>(
        `settings/workexperiencetype/status/${categoryId}`,
        {},
      )
      .pipe(
        tap({
          next: (response) => {
            if (response.status) {
              this.showSuccessToast(response?.message);
              this.fetchEventCategoryList().subscribe();
            } else {
              this.showErrorToast(
                response?.message ||
                  'Something went wrong. Please try again later.',
              );
            }
          },
          error: (err) => {
            console.error('Failed to delete event category:', err);
            this.showErrorToast(
              err?.message || 'Something went wrong. Please try again later.',
            );
          },
        }),
      )
      .subscribe();
  }

  fetchEventCategoryList() {
    this.isLoading = true;
    return this.httpService
      .get<ApiDataResponse>('settings/workexperiencetype/get_all')
      .pipe(
        tap({
          next: (response) => {
            if (response.status && response.data) {
              this.eventCategories = this.transformApiData(response.data);
              this.filteredEventCategories = [...this.eventCategories];
            } else {
              this.eventCategories = [];
              this.filteredEventCategories = [];
            }
            this.isLoading = false;
          },
          error: (err) => {
            console.error('Failed to load event categories:', err);
            this.showErrorToast(err?.message);
            this.isLoading = false;
            this.eventCategories = [];
            this.filteredEventCategories = [];
          },
        }),
      );
  }

  private transformApiData(apiData: EventCategoryResponse[]): EventCategory[] {
    return apiData.map((item) => ({
      id: item.id,
      name: item.categoryName,
      typeName: item.typeName,
      description: item.description,
      isActive: item.isActive,
      isOpen: false,
      status: 'Inactive',
    }));
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
